import { useEffect, useMemo, useRef, useState } from 'react'
import { COLORS } from 'app-constants'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useSearchParams } from 'react-router-dom'
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { LineChart } from '@mui/x-charts/LineChart'
import { useRecoilValue } from 'recoil'
import { myTwinAtom } from 'state/my-twin'
import { useFetchCompanies, useFetchTrends, useFetchTrendsTopicTypes } from 'hooks/use-trends'
import { Trend, TrendDatapoint } from 'api/model'
import styled from '@emotion/styled'
import { ReferenceItem, getReferenceIdMap } from '../InsightsList'
import { formatMonthYear } from 'utils'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AcquisitionBanner from 'components/AcquisitionBanner/AcquisitionBanner'

const TrendCardStack = styled(Stack)`
  background-color: ${COLORS.PURE_WHITE};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`

const TimeBadge = ({ time }: { time: string }) => {
  return (
    <Stack
      p="4px 10px"
      borderRadius="20px"
      sx={{
        background: COLORS.JELLED_MID_BLUE_10_PERCENT,
      }}>
      <Typography
        variant="pXS"
        sx={{
          color: COLORS.JELLED_PRIMARY_BLUE,
        }}>
        {time}
      </Typography>
    </Stack>
  )
}

// write a function that inserts commas in a number
function numberWithCommas(x: number | string) {
  if (typeof x === 'string') {
    return x.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const TrendData = ({ data }: { data: TrendDatapoint[] }) => {
  // if there is no data, return null
  if (data.length === 0 || (!data[0].string_value && !data[0].value)) {
    return null
  }

  if (data.length === 1) {
    let displayedValue = ''
    if (data[0].string_value) {
      displayedValue = numberWithCommas(data[0].string_value)
    } else {
      displayedValue = numberWithCommas(data[0].value ?? 0)
    }

    return (
      <Stack direction="row" spacing={'4px'} mt={2}>
        <Typography variant="h3">{displayedValue}</Typography>
        <Typography
          variant="pXS"
          sx={{
            lineHeight: '24px',
            alignSelf: 'flex-end',
          }}>
          {data[0].unit === 'none' || (data[0].unit && displayedValue.includes(data[0].unit)) ? '' : data[0].unit}
        </Typography>
      </Stack>
    )
  }

  const xData = data.map((datapoint) => new Date(datapoint.date))
  const minValue = Math.min(...data.map((datapoint) => datapoint.value || 0))
  const maxValue = Math.max(...data.map((datapoint) => datapoint.value || 0))

  const yData: number[] = data.map((datapoint) => datapoint.value || 0)
  const minYValue = Math.min(...yData)
  const maxYValue = Math.max(...yData)

  // used to make the graph less steep
  const minYTick = minYValue * 0.25 // 75% less than minValue
  const maxYTick = maxYValue * 1.75 // 75% greater than maxValue

  return (
    <Stack mt={2}>
      <LineChart
        xAxis={[
          { data: xData, scaleType: 'time' },
          { min: minValue, max: maxValue },
        ]}
        yAxis={[{ min: minYTick, max: maxYTick }]}
        series={[
          {
            data: yData,
          },
        ]}
        width={500}
        height={300}
        margin={{ left: 100 }}
      />
    </Stack>
  )
}

// write a function that formats the time as just the year
function formatYear(date: string) {
  return date.split('-')[0]
}

// it is possible that we have data points with the same date and value so we need to filter them out
function filterUniqueDatapoints(datapoints: TrendDatapoint[]) {
  const uniqueDatapointsMap = new Map()

  datapoints.forEach((datapoint) => {
    const key = `${datapoint.date}-${datapoint.value}`
    if (!uniqueDatapointsMap.has(key)) {
      uniqueDatapointsMap.set(key, datapoint)
    }
  })

  const filtered = Array.from(uniqueDatapointsMap.values())

  // sort by ascending timestamp
  return filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
}

const TrendCard = ({ trend }: { trend: Trend }) => {
  const getTrendTime = () => {
    if (trend.is_date_year) {
      // return just the year if it's a year trend
      return formatYear(trend.date_range?.date_start || '')
    }

    if (trend.date_range) {
      return `${formatMonthYear(new Date(trend.date_range.date_start))} - ${formatMonthYear(
        new Date(trend.date_range.date_end)
      )}`
    }
    if (trend.date) {
      return formatMonthYear(new Date(trend.date))
    }

    return ''
  }

  const time = getTrendTime()

  const referenceIdMap = getReferenceIdMap(trend.references)

  const uniqueTrendData = filterUniqueDatapoints(trend.data)

  return (
    <TrendCardStack
      sx={{
        scrollMarginTop: '112px', // this is to make sure the card doesn't get hidden behind the header
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">{trend.name}</Typography>

        {time && <TimeBadge time={time} />}
      </Stack>

      {trend.description !== 'none' && trend.description !== 'N/A' && (
        <Typography variant="pS" mt={3}>
          {trend.description}
        </Typography>
      )}

      {trend.data.length > 0 && <TrendData data={uniqueTrendData} />}

      <Stack direction="row" alignItems="center" flexWrap="wrap" mt={3} gap={1}>
        {trend.references?.map((reference) => (
          <ReferenceItem key={reference.title} reference={reference} referenceIdMap={referenceIdMap} />
        ))}
      </Stack>
    </TrendCardStack>
  )
}

const TrendsList = ({ trends, trendIdFromUrl }: { trends: Trend[]; trendIdFromUrl: string | null }) => {
  // auto scroll to the trend with the id from the url
  const trendToScrollToRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // setTimeout is needed because the DOM needs to be updated before we can scroll to the bottom
    setTimeout(() => {
      if (trendToScrollToRef.current) {
        trendToScrollToRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }, 100)
  }, [trends, trendIdFromUrl])

  return (
    <Stack mt={3}>
      {trends.map((trend) => (
        <Stack key={trend.name} ref={trend.id === trendIdFromUrl ? trendToScrollToRef : null}>
          <TrendCard trend={trend} />
        </Stack>
      ))}
    </Stack>
  )
}

function displayTopicType(topicType: string) {
  return topicType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const CompanyTrends = () => {
  const myTwin = useRecoilValue(myTwinAtom)
  const { companies, isLoading: isLoadingCompanies } = useFetchCompanies(myTwin?.id || '')

  // get the id of the company from the query params
  const [searchParams] = useSearchParams()
  const companyName = searchParams.get('name')
  const topicType = searchParams.get('topicType')
  const trendId = searchParams.get('trendId')
  const companyDisplayName = companies.find((company) => company.system_name === companyName)?.name

  const { trendsTopicTypes, isLoading: isLoadingTrendsTopicTypes } = useFetchTrendsTopicTypes(
    myTwin?.id ?? '',
    companyName ?? ''
  )

  // sort topicTypes tabs based on whether or not they are in trendsTopicTypes
  const topicTypes = useMemo(() => {
    return myTwin?.trends
      ? [...myTwin?.trends].sort((a, b) => {
          if (trendsTopicTypes.includes(a) && !trendsTopicTypes.includes(b)) {
            return -1
          }
          if (!trendsTopicTypes.includes(a) && trendsTopicTypes.includes(b)) {
            return 1
          }
          return 0
        })
      : []
  }, [myTwin?.trends, trendsTopicTypes])

  const [tabValue, setTabValue] = useState(0)

  useEffect(() => {
    if (topicType) {
      const index = topicTypes.findIndex((tt) => tt === topicType)
      if (index !== -1) {
        setTabValue(index)
      }
    }
  }, [topicType, topicTypes])

  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  // topicTypes is sorted, but the topicTypes[tabValue] may not be a value that exists in trendsTopicTypes
  // so we need to make sure that it is in trendsTopicTypes
  // because we don't want to be making a fetch for a topic that is not within topicTypes
  const { trends, isLoading: isLoadingTrends } = useFetchTrends(
    myTwin?.id || '',
    companyName || '',
    topicTypes.length > 0 && trendsTopicTypes.includes(topicTypes[tabValue]) ? topicTypes[tabValue] : ''
  )

  const isMobile = useCheckMobileScreen()

  const [searchText, setSearchText] = useState('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredTrends = trends.filter((trend) => trend.name.toLowerCase().includes(searchText.toLowerCase()))

  const renderContent = () => {
    if (isLoadingTrends || isLoadingCompanies || isLoadingTrendsTopicTypes) {
      return (
        <Stack justifyContent="center" alignItems="center" height="calc(100vh - 260px)" width="100%">
          <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
        </Stack>
      )
    }

    return (
      <Stack>
        {filteredTrends.length > 0 ? (
          <TrendsList trends={filteredTrends} trendIdFromUrl={trendId} />
        ) : (
          <Typography variant="pS" mt={3}>
            No trends found
          </Typography>
        )}
      </Stack>
    )
  }

  return (
    <Stack
      sx={{
        background: isMobile ? COLORS.GRAY_8 : 'transparent',
        height: isMobile ? '100vh' : 'auto',
      }}>
      {!isMobile && (
        <Stack
          py="40px"
          pl="41px"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
            borderBottom: `1px solid ${COLORS.GRAY_5}`,
          }}>
          <Typography variant="h4">Trends</Typography>

          <TextField
            placeholder="Search trends"
            value={searchText}
            onChange={onSearchTextChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: COLORS.GRAY_3,
                      marginRight: '-10px',
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? '100%' : '400px',
              marginRight: isMobile ? '0' : '24px',
              input: {
                color: 'secondary.main',
              },
              '& ::placeholder': {
                color: 'secondary.main',
              },
            }}
          />
        </Stack>
      )}

      <Stack mb={3}>
        <AcquisitionBanner />
      </Stack>

      <Stack
        sx={{
          px: isMobile ? '16px' : '24px',
          pt: isMobile ? '16px' : '0',
          maxWidth: isMobile ? 'inherit' : '100%',
        }}>
        <Stack direction="row" alignItems="center" spacing="4px" mb={2}>
          <IconButton
            onClick={() => {
              window.history.back()
            }}>
            <ArrowBackIcon
              sx={{
                fill: COLORS.GRAY_3,
              }}
            />
          </IconButton>

          <Typography
            variant="h5"
            sx={{
              color: COLORS.GRAY_0,
            }}>
            {companyDisplayName}
          </Typography>
        </Stack>
        <Tabs
          orientation="horizontal"
          value={tabValue}
          onChange={onTabChange}
          textColor="secondary"
          sx={{
            '.MuiTabs-flexContainer': {
              gap: isMobile ? 0 : 4,
            },
          }}>
          {topicTypes.map((tt, index) => (
            <Tab
              key={index}
              style={{ pointerEvents: 'auto' }}
              disabled={!trendsTopicTypes.includes(tt)}
              icon={
                <Tooltip title={trendsTopicTypes.includes(tt) ? '' : 'No trends to show yet'}>
                  <span>{displayTopicType(tt)}</span>
                </Tooltip>
              }
              sx={{
                fontWeight: 500,
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              }}
            />
          ))}
        </Tabs>

        {renderContent()}
      </Stack>
    </Stack>
  )
}

export default CompanyTrends
